import { Component, createRef } from 'preact';
import StoneButton from './StoneButton';
import { t } from '../../localization/t';

// Start-phase draw prompt rendered as pile-anchored buttons.
//
// Two buttons (Draw a Spell, Draw a Site) are wrapped in
// position:fixed shells; GameBoard's per-frame projector mutates
// `left` / `top` / `transform` on those shells to keep them centered
// over the matching Spellbook / Atlas pile meshes and scaled with
// the rendered pile size so they don't overpower a zoomed-out pile.
//
// Button sizing uses cqw so the base size tracks the canvas, and the
// projector applies a scale multiplier on top of that to track 3D
// camera zoom. No cancel option. The draw is mandatory in Sorcery.
//
// Each button shows a keycap-styled hotkey indicator (1 for Atlas,
// 2 for Spellbook). The same keys are intercepted by GameBoard's
// `handleGameHotkey` while the prompt is active, taking precedence
// over the generic "draw N from hovered pile" number binding.

const KBD_STYLE = {
  display: 'inline-block',
  padding: '0.05cqw 0.4cqw',
  border: '0.0625cqw solid rgba(232, 195, 110, 0.85)',
  borderRadius: 'var(--radius-sm)',
  background: 'rgba(0, 0, 0, 0.45)',
  fontFamily: '"Courier New", ui-monospace, monospace',
  fontSize: '0.95cqw',
  fontWeight: 700,
  lineHeight: 1,
  color: 'rgba(255, 230, 170, 0.95)',
  textShadow: '0 1px 1px rgba(0, 0, 0, 0.7)',
  letterSpacing: 0,
};

const LABEL_ROW_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.6cqw',
  whiteSpace: 'nowrap',
  fontSize: '1.4cqw',
};

export default class StartDrawOverlay extends Component {
  spellRef = createRef();
  atlasRef = createRef();

  componentDidMount() {
    this.props.registerRefs?.({
      spell: this.spellRef,
      atlas: this.atlasRef,
    });
  }

  componentWillUnmount() {
    this.props.registerRefs?.(null);
  }

  render() {
    const { spellCount, atlasCount, singleDraw, onChoose } = this.props;

    const wrapStyle = {
      position: 'fixed',
      display: 'none',
      transform: 'translate(-50%, -50%)',
      transformOrigin: 'center center',
      opacity: 0,
      transition: 'opacity 200ms ease',
      pointerEvents: 'auto',
      zIndex: 70,
    };

    // Magician (no atlas) collapses to a single "Draw Card" button
    // anchored over the spellbook. The atlas shell is omitted entirely;
    // the projector tolerates a missing ref and hides the spellbook
    // button only when the prompt is gone.
    const spellLabel = singleDraw
      ? t('Draw Card ({n})', { n: spellCount })
      : t('Draw Spell ({n})', { n: spellCount });
    const spellHotkey = singleDraw ? '1' : '2';

    return (
      <>
        {!singleDraw && (
          <div ref={this.atlasRef} style={wrapStyle}>
            <StoneButton
              variant="primary"
              widthCqw={16}
              heightCqw={4}
              onClick={() => onChoose('atlas')}
            >
              <span style={LABEL_ROW_STYLE}>
                <span style={KBD_STYLE}>1</span>
                <span>{t('Draw Site ({n})', { n: atlasCount })}</span>
              </span>
            </StoneButton>
          </div>
        )}
        <div ref={this.spellRef} style={wrapStyle}>
          <StoneButton
            variant="primary"
            widthCqw={16}
            heightCqw={4}
            onClick={() => onChoose('spellbook')}
          >
            <span style={LABEL_ROW_STYLE}>
              <span style={KBD_STYLE}>{spellHotkey}</span>
              <span>{spellLabel}</span>
            </span>
          </StoneButton>
        </div>
      </>
    );
  }
}
